import React, { useEffect, useState } from 'react'
import intl from 'react-intl-universal'
import { array, func, object } from 'prop-types'
import TemplateList from './TemplatesList.js'
import { getEvidentlyFeatures } from '~/evidentlyFeatureFlags'
import { NEW_EDITOR_EXP } from '~/util/constants/keys'

import './styles.scss'
import BlankTemplate from './BlankTemplate.js'

export default function CreateStory({
  defaultTemplate = [],
  starredTemplates = [],
  browseAllTemplates,
  canCreateStory = true,
  hasBlankTemplate = false,
  composerSettings,
}) {
  const [starredTemplateWithHrefs, setStarredTemplateWithHrefs] = useState([])
  const [defaultTemplateWithHrefs, setDefaultTemplateWithHrefs] = useState([])

  async function setHref(templateId) {
    const flags = await getEvidentlyFeatures()
    let v2Enabled = false
    const userEnabledNewEditor = window.localStorage.getItem(NEW_EDITOR_EXP)
    if (flags['v2-open-beta'] && composerSettings?.openBeta?.enabled) {
      if (userEnabledNewEditor === undefined || userEnabledNewEditor === null) {
        v2Enabled = composerSettings?.openBeta?.default ?? false
      } else {
        v2Enabled = userEnabledNewEditor === 'true'
      }
    }

    if (flags['ui-v1-dropoff']) {
      return `/composer/story/v2/new?template=${templateId}`
    }

    return v2Enabled || flags['ui-v2-release']
      ? `/composer/story/v2/new?template=${templateId}`
      : `/composer/story?template=${templateId}`
  }

  useEffect(() => {
    async function mapTemplates() {
      const templateList = await Promise.all(
        defaultTemplate.map(async (template) => {
          const _href = await setHref(template.id)
          return {
            id: template.id,
            name: template.name,
            href: _href,
          }
        })
      )
      setDefaultTemplateWithHrefs(templateList)
    }

    mapTemplates()
  }, [defaultTemplate])

  useEffect(() => {
    async function mapTemplates() {
      const templateList = await Promise.all(
        starredTemplates.map(async (template) => {
          const _href = await setHref(template.id)
          return {
            id: template.id,
            name: template.name,
            href: _href,
          }
        })
      )
      setStarredTemplateWithHrefs(templateList)
    }

    mapTemplates()
  }, [starredTemplates])

  if (
    defaultTemplate.length === 0 &&
    starredTemplates.length === 0 &&
    !hasBlankTemplate
  ) {
    return (
      <>
        <button
          className="btn btn-success pull-right"
          id="createStoryButton"
          data-testid="createStoryButton"
          onClick={browseAllTemplates}
          disabled={!canCreateStory}
        >
          {!canCreateStory && (
            <i
              className="fa fa-info-circle fa-lg"
              data-toggle="tooltip"
              data-placement="top"
              data-testid="canCreateStoryPopup"
              data-original-title={intl.get(
                'search.template.createStoryTooltip'
              )}
              title={intl.get('search.template.createStoryTooltip')}
            ></i>
          )}
          {intl.get('templateSelection.createStoryButton')}
        </button>
      </>
    )
  }

  return (
    <div className="dropdown">
      <button
        className="btn btn-info float-right dropdown-toggle"
        type="button"
        id="createStoryButton"
        data-testid="createStoryButton"
        data-toggle="dropdown"
        aria-expanded={false}
        disabled={!canCreateStory}
      >
        {!canCreateStory && (
          <i
            className="fa fa-info-circle fa-lg"
            data-toggle="tooltip"
            data-placement="top"
            data-testid="canCreateStoryPopup"
            data-original-title={intl.get('search.template.createStoryTooltip')}
            title={intl.get('search.template.createStoryTooltip')}
          ></i>
        )}
        {intl.get('templateSelection.createStoryButton')}
        <i className="fa fa-angle-down" aria-hidden="true"></i>
      </button>
      <div
        className="dropdown-menu"
        aria-labelledby="createStoryButton"
        id="createStoryDropdown"
        data-testid="createStoryDropdown"
      >
        {hasBlankTemplate && (
          <BlankTemplate
            composerSettings={composerSettings}
            isOnlyOption={
              starredTemplates.length === 0 && defaultTemplate.length === 0
            }
          />
        )}
        <TemplateList
          templateList={defaultTemplateWithHrefs}
          headerTitle={intl.get('search.template.defaultTemplate')}
          icon="fa-map-pin"
          type="default"
          testid="defaultTemplate"
          isStarred={starredTemplates.length === 0}
        />
        <TemplateList
          templateList={starredTemplateWithHrefs}
          headerTitle={intl.get('search.template.starredTemplates')}
          icon="fa-star"
          type="starred"
          testid="starredTemplates"
          isStarred={true}
        />
        <button
          className="btn btn-outline-dark d-block mx-auto mb-2"
          data-testid="browseAllTemplateButton"
          onClick={browseAllTemplates}
        >
          {intl.get('search.template.browseAllTemplates')}
        </button>
      </div>
    </div>
  )
}

CreateStory.propTypes = {
  defaultTemplate: array,
  starredTemplates: array,
  browseAllTemplates: func,
  composerSettings: object,
}
